import { AddressBookAction, AddressHash } from '~/reducers/address/address.reducer';
import APIAddress, { SavedAPIAddress } from '~/types/APIAddress';
import { ActionKeys } from './constants';
import { TrackingReference } from '~/utils/TrackingHelper/types';

export interface FetchAddressesPayload {
  addresses: SavedAPIAddress[];
}

export interface FetchAddressesErrorPayload {
  error: string;
}

interface CreateAPIAddressPayloadBase {
  key?: string;
  addressBookName?: string;
}

export interface CreateAPIAddressPayload extends CreateAPIAddressPayloadBase {
  address: APIAddress;
  trackingReference: TrackingReference;
}

export interface CreateAPIAddressSuccessPayload extends CreateAPIAddressPayloadBase {
  address: SavedAPIAddress;
  trackingReference: TrackingReference;
}

// Edit
export interface CreateAPIAddressErrorPayload {
  error: string;
}

export interface EditAPIAddressPayload {
  id?: number;
  address: APIAddress;
  trackingReference?: TrackingReference;
}

export interface EditAPIAddressErrorPayload {
  error: string;
}

export interface DeleteAPIAddressPayload {
  id: number;
  key?: Maybe<string>;
  trackingReference: TrackingReference;
}

export interface DeleteAPIAddressErrorPayload {
  error: string;
}

// Fetch
export function fetchAddresses() {
  return <const>{
    type: ActionKeys.ADDRESSES_FETCH,
  };
}

export function fetchAddressesSuccess(payload: FetchAddressesPayload) {
  return <const>{
    payload,
    type: ActionKeys.ADDRESSES_FETCH_SUCCESS,
  };
}

export function fetchAddressesError(payload: FetchAddressesErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.ADDRESSES_FETCH_ERROR,
  };
}

// Create
export function createAPIAddress(payload: CreateAPIAddressPayload) {
  return <const>{
    payload,
    type: ActionKeys.APIADDRESS_CREATE,
  };
}

export function createAPIAddressSuccess(payload: CreateAPIAddressSuccessPayload) {
  return <const>{
    payload,
    type: ActionKeys.APIADDRESS_CREATE_SUCCESS,
  };
}

export function createAPIAddressError(payload: CreateAPIAddressErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.APIADDRESS_CREATE_ERROR,
  };
}

// Edit
export function editAPIAddress(payload: EditAPIAddressPayload) {
  return <const>{
    payload,
    type: ActionKeys.APIADDRESS_EDIT,
  };
}

export function editAPIAddressSuccess(payload: EditAPIAddressPayload) {
  return <const>{
    payload,
    type: ActionKeys.APIADDRESS_EDIT_SUCCESS,
  };
}

export function editAPIAddressError(payload: EditAPIAddressErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.APIADDRESS_EDIT_ERROR,
  };
}

// Delete
export function deleteAPIAddress(payload: DeleteAPIAddressPayload) {
  return <const>{
    payload,
    type: ActionKeys.APIADDRESS_DELETE,
  };
}

export function deleteAPIAddressSuccess(id: number) {
  return <const>{
    type: ActionKeys.APIADDRESS_DELETE_SUCCESS,
    payload: {
      id,
    },
  };
}

export function deleteAPIAddressError(payload: DeleteAPIAddressErrorPayload) {
  return <const>{
    payload,
    type: ActionKeys.APIADDRESS_DELETE_ERROR,
  };
}

export function addressBookInitialize(
  addressBookName: string,
  addresses: AddressHash,
  combineSenderReturnAddresses?: boolean, // Using this parameter will manually configure combineSenderReturnAddresses instead of letting the reducer decide
) {
  return <const>{
    type: ActionKeys.ADDRESS_BOOK_INITIALIZE,
    payload: {
      addresses,
      addressBookName,
      combineSenderReturnAddresses,
    },
  };
}

export function addressBookPerformAction(
  addressBookName: string,
  action: Maybe<AddressBookAction>,
) {
  return <const>{
    type: ActionKeys.ADDRESS_BOOK_ACTION,
    payload: {
      action,
      addressBookName,
    },
  };
}

export function addressBookSelectAddress(
  addressBookName: string,
  key: string,
  address: number | APIAddress,
) {
  return <const>{
    type: ActionKeys.ADDRESS_BOOK_SELECT,
    payload: {
      key,
      address,
      addressBookName,
    },
  };
}

export function addressBookClearSelection(addressBookName: string, key: string) {
  return <const>{
    type: ActionKeys.ADDRESS_BOOK_CLEAR_SELECTION,
    payload: {
      key,
      addressBookName,
    },
  };
}

export function addressBookSelectDefaultAddress(addressBookName: string, key: string) {
  return <const>{
    type: ActionKeys.ADDRESS_BOOK_SELECT_DEFAULT,
    payload: {
      key,
      addressBookName,
    },
  };
}

export function toggleCombineSenderReturnAddresses(addressBookName: string) {
  return <const>{
    type: ActionKeys.ADDRESS_BOOK_TOGGLE_COMBINE_SENDER_RETURN_ADDRESSES,
    payload: {
      addressBookName,
    },
  };
}

export function addressBookClearState(addressBookName: string) {
  return <const>{
    type: ActionKeys.ADDRESS_BOOK_CLEAR_STATE,
    payload: {
      addressBookName,
    },
  };
}

export type AddressActions =
  | ReturnType<typeof fetchAddresses>
  | ReturnType<typeof fetchAddressesSuccess>
  | ReturnType<typeof fetchAddressesError>
  | ReturnType<typeof createAPIAddress>
  | ReturnType<typeof createAPIAddressSuccess>
  | ReturnType<typeof createAPIAddressError>
  | ReturnType<typeof editAPIAddress>
  | ReturnType<typeof editAPIAddressSuccess>
  | ReturnType<typeof editAPIAddressError>
  | ReturnType<typeof deleteAPIAddress>
  | ReturnType<typeof deleteAPIAddressSuccess>
  | ReturnType<typeof deleteAPIAddressError>
  // Address book actions
  | ReturnType<typeof addressBookInitialize>
  | ReturnType<typeof addressBookPerformAction>
  | ReturnType<typeof addressBookSelectAddress>
  | ReturnType<typeof addressBookClearSelection>
  | ReturnType<typeof addressBookSelectDefaultAddress>
  | ReturnType<typeof toggleCombineSenderReturnAddresses>
  | ReturnType<typeof addressBookClearState>;
